import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  FileText, Upload, Download, Trash2, Plus, Calendar as CalendarIcon,
  CheckCircle, Users, Camera, Eye
} from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";
import { handleDownloadFile as downloadFile, toggleStudentSelection } from "./utils";

export function AssignmentsTab({ classId, assignments, assignmentSubmissions, students, isTeacher, headers, onRefresh }) {
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [submissionsDialog, setSubmissionsDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);
  const [selectedAssignmentSubmissions, setSelectedAssignmentSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ title: "", description: "", due_date: null, total_points: 100, attachment: null });
  const [uploadingAssignmentFile, setUploadingAssignmentFile] = useState(false);
  const [assignShareMode, setAssignShareMode] = useState("all");
  const [assignSelectedStudents, setAssignSelectedStudents] = useState([]);

  const assignmentFileRef = useRef(null);
  const assignmentAttachmentRef = useRef(null);
  const cameraInputRef = useRef(null);
  const assignCameraRef = useRef(null);

  const handleCreateAssignment = async () => {
    if (!newAssignment.title) {
      toast.error("Please enter a title");
      return;
    }
    try {
      let attachmentUrl = null;
      let attachmentName = null;

      if (newAssignment.attachment) {
        setUploadingAssignmentFile(true);
        const formData = new FormData();
        formData.append("file", newAssignment.attachment);
        const uploadRes = await axios.post(`${API}/upload-file`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        attachmentUrl = `${API}/files/${uploadRes.data.file_id}/download`;
        attachmentName = newAssignment.attachment.name;
        setUploadingAssignmentFile(false);
      }

      const sharedWith = assignShareMode === "specific" && assignSelectedStudents.length > 0
        ? assignSelectedStudents : null;

      await axios.post(`${API}/assignments`, {
        class_id: classId,
        title: newAssignment.title,
        description: newAssignment.description,
        due_date: newAssignment.due_date ? newAssignment.due_date.toISOString() : null,
        total_points: newAssignment.total_points,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
        shared_with: sharedWith
      }, { headers });
      toast.success("Assignment created!");
      setNewAssignment({ title: "", description: "", due_date: null, total_points: 100, attachment: null });
      setAssignmentDialog(false);
      setAssignShareMode("all");
      setAssignSelectedStudents([]);
      if (assignmentAttachmentRef.current) assignmentAttachmentRef.current.value = "";
      if (assignCameraRef.current) assignCameraRef.current.value = "";
      onRefresh();
    } catch (error) {
      toast.error("Failed to create assignment");
      setUploadingAssignmentFile(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId, title) => {
    if (!confirm(`Delete "${title}"? This will also delete all student submissions.`)) return;
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`, { headers });
      toast.success("Assignment deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete assignment");
    }
  };

  const handleViewSubmissions = async (assignment) => {
    setSelectedAssignment(assignment);
    setLoadingSubmissions(true);
    setSubmissionsDialog(true);
    try {
      const response = await axios.get(`${API}/assignments/${assignment.assignment_id}/submissions`, { headers });
      const submissionsWithStudents = response.data.map((sub) => {
        const studentInfo = students.find(s => s.user_id === sub.student_id);
        return { ...sub, student_name: studentInfo?.name || "Unknown", student_email: studentInfo?.email || "" };
      });
      setSelectedAssignmentSubmissions(submissionsWithStudents);
    } catch (error) {
      toast.error("Failed to load submissions");
      setSubmissionsDialog(false);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleGradeSubmission = async (submissionId, currentGrade, totalPoints) => {
    const gradeStr = prompt(`Enter grade (out of ${totalPoints}):`, currentGrade || "");
    if (gradeStr === null) return;
    const grade = parseInt(gradeStr);
    if (isNaN(grade) || grade < 0 || grade > totalPoints) {
      toast.error(`Grade must be between 0 and ${totalPoints}`);
      return;
    }
    const feedback = prompt("Enter feedback (optional):", "");
    try {
      await axios.put(`${API}/submissions/${submissionId}/grade`, { grade, feedback: feedback || "" }, { headers });
      toast.success("Grade saved!");
      if (selectedAssignment) handleViewSubmissions(selectedAssignment);
      onRefresh();
    } catch (error) {
      toast.error("Failed to save grade");
    }
  };

  const [submitFiles, setSubmitFiles] = useState([]);

  const handleSubmitFilesChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length > 0) {
      setSubmitFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeSubmitFile = (index) => {
    setSubmitFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitAssignment = async () => {
    if (submitFiles.length === 0 || !selectedAssignment) return;
    setSubmittingAssignment(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < submitFiles.length; i++) {
        formData.append("files", submitFiles[i]);
      }
      formData.append("comment", "");
      const res = await axios.post(`${API}/assignments/${selectedAssignment.assignment_id}/submit`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      const added = res.data?.new_files_added ?? submitFiles.length;
      const total = res.data?.file_count ?? submitFiles.length;
      const isResubmit = !!assignmentSubmissions[selectedAssignment.assignment_id];
      toast.success(
        isResubmit
          ? `Added ${added} more file(s). Total: ${total} file(s) submitted.`
          : `Assignment submitted with ${total} file(s)!`
      );
      setSubmitDialog(false);
      setSelectedAssignment(null);
      setSubmitFiles([]);
      onRefresh();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit assignment. Try uploading fewer/smaller files.");
    } finally {
      setSubmittingAssignment(false);
      if (assignmentFileRef.current) assignmentFileRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const openSubmitDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setSubmitFiles([]);
    setSubmitDialog(true);
  };

  const handleDownloadAttachment = (url, filename) => {
    downloadFile(url, filename, headers);
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <CardTitle className="font-['Manrope']">Assignments</CardTitle>
          <CardDescription>Deadlines and tasks</CardDescription>
        </div>
        {isTeacher && (
          <Dialog open={assignmentDialog} onOpenChange={setAssignmentDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="create-assignment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Assignment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Create Assignment</DialogTitle>
                <DialogDescription>Set a new assignment with deadline</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input data-testid="assignment-title-input" placeholder="Assignment title" value={newAssignment.title} onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea data-testid="assignment-description-input" placeholder="Assignment details..." rows={3} value={newAssignment.description} onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Due Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !newAssignment.due_date && "text-muted-foreground")} data-testid="due-date-picker">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newAssignment.due_date ? format(newAssignment.due_date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={newAssignment.due_date} onSelect={(date) => setNewAssignment({ ...newAssignment, due_date: date })} initialFocus />
                      {newAssignment.due_date && (
                        <div className="p-2 border-t">
                          <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setNewAssignment({ ...newAssignment, due_date: null })}>
                            Clear date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Total Points</Label>
                  <Input data-testid="total-points-input" type="number" value={newAssignment.total_points} min="0" onChange={(e) => { const v = parseInt(e.target.value); setNewAssignment({ ...newAssignment, total_points: isNaN(v) ? 0 : v }); }} />
                </div>
                <div className="space-y-2">
                  <Label>Attachment (Optional)</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                    <input type="file" ref={assignmentAttachmentRef} onChange={(e) => setNewAssignment({ ...newAssignment, attachment: e.target.files?.[0] || null })} className="hidden" />
                    <input type="file" accept="image/*" capture="environment" ref={assignCameraRef} onChange={(e) => setNewAssignment({ ...newAssignment, attachment: e.target.files?.[0] || null })} className="hidden" />
                    {newAssignment.attachment ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground">{newAssignment.attachment.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => { setNewAssignment({ ...newAssignment, attachment: null }); if (assignmentAttachmentRef.current) assignmentAttachmentRef.current.value = ""; if (assignCameraRef.current) assignCameraRef.current.value = ""; }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-2">Upload file or take a photo</p>
                        <div className="flex items-center justify-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => assignmentAttachmentRef.current?.click()}>
                            <FileText className="w-4 h-4 mr-1" />
                            Choose File
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => assignCameraRef.current?.click()} data-testid="assign-camera-btn">
                            <Camera className="w-4 h-4 mr-1" />
                            Take Photo
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {students.length > 0 && (
                  <div className="space-y-2">
                    <Label>Share with</Label>
                    <div className="flex gap-3">
                      <button onClick={() => setAssignShareMode("all")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${assignShareMode === "all" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="assign-share-all-btn">
                        All Students
                      </button>
                      <button onClick={() => setAssignShareMode("specific")} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${assignShareMode === "specific" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="assign-share-specific-btn">
                        Specific Students
                      </button>
                    </div>
                    {assignShareMode === "specific" && (
                      <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                        {students.map(s => (
                          <label key={s.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                            <input type="checkbox" checked={assignSelectedStudents.includes(s.user_id)} onChange={() => toggleStudentSelection(s.user_id, setAssignSelectedStudents, assignSelectedStudents)} className="rounded border-border" />
                            <span>{s.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{s.email}</span>
                          </label>
                        ))}
                        {assignSelectedStudents.length > 0 && (
                          <p className="text-xs text-primary px-1 pt-1">{assignSelectedStudents.length} student(s) selected</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={handleCreateAssignment} className="w-full rounded-full bg-primary text-primary-foreground" data-testid="submit-assignment-btn" disabled={uploadingAssignmentFile}>
                  {uploadingAssignmentFile ? "Uploading..." : "Create Assignment"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {assignments.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No assignments yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {assignments.map((assignment) => {
              const isPastDue = assignment.due_date ? new Date(assignment.due_date) < new Date() : false;
              const submission = assignmentSubmissions[assignment.assignment_id];
              const isSubmitted = !!submission;

              return (
                <motion.div
                  key={assignment.assignment_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-4 rounded-xl border transition-all ${
                    isSubmitted ? "border-green-200 bg-green-50/50" :
                    isPastDue ? "border-destructive/30 bg-destructive/5" : "border-border/50"
                  }`}
                  data-testid={`assignment-${assignment.assignment_id}`}
                >
                  <div className="space-y-3">
                    {/* Title row with status badges */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-foreground">{assignment.title}</h3>
                          {isSubmitted && (
                            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                              <CheckCircle className="w-3 h-3" />
                              Submitted
                            </span>
                          )}
                          {isPastDue && !isSubmitted && !isTeacher && (
                            <span className="px-2 py-1 text-xs bg-destructive/10 text-destructive rounded-full whitespace-nowrap">
                              Past Due
                            </span>
                          )}
                        </div>
                        {assignment.description && (
                          <p className="text-sm text-muted-foreground mt-1">{assignment.description}</p>
                        )}
                      </div>
                      {/* Action buttons */}
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {isTeacher && (
                          <>
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleViewSubmissions(assignment)} data-testid={`view-submissions-${assignment.assignment_id}`}>
                              <Users className="w-4 h-4 mr-1" />
                              Submissions
                            </Button>
                            <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDeleteAssignment(assignment.assignment_id, assignment.title)} data-testid={`delete-assignment-${assignment.assignment_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {!isTeacher && !isSubmitted && (
                          <Button
                            size="sm"
                            onClick={() => openSubmitDialog(assignment)}
                            className={`rounded-full ${isPastDue ? "bg-orange-500 hover:bg-orange-600" : "bg-primary"} text-primary-foreground`}
                            data-testid={`submit-assignment-${assignment.assignment_id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {isPastDue ? "Submit Late" : "Submit"}
                          </Button>
                        )}
                        {!isTeacher && isSubmitted && (
                          <Button size="sm" variant="outline" onClick={() => openSubmitDialog(assignment)} className="rounded-full" data-testid={`resubmit-${assignment.assignment_id}`}>
                            <Upload className="w-4 h-4 mr-1" />
                            Add More Files
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Metadata row - wraps on mobile */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      {assignment.due_date && (
                        <span className={`whitespace-nowrap ${isPastDue && !isSubmitted ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                          Due: {format(new Date(assignment.due_date), "MMM d, h:mm a")}
                        </span>
                      )}
                      <span className="text-muted-foreground whitespace-nowrap">{assignment.total_points} points</span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        Uploaded: {format(new Date(assignment.created_at), "MMM d, yyyy")}
                      </span>
                      {submission?.grade !== null && submission?.grade !== undefined && (
                        <span className="text-green-600 font-medium whitespace-nowrap">
                          Grade: {submission.grade}/{assignment.total_points}
                        </span>
                      )}
                    </div>

                    {isTeacher && assignment.shared_with && assignment.shared_with.length > 0 && (
                      <p className="text-xs text-blue-600 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Shared with {assignment.shared_with.length} student(s)
                      </p>
                    )}
                    {assignment.attachment_url && (
                      <div>
                        <button
                          onClick={() => handleDownloadAttachment(assignment.attachment_url, assignment.attachment_name)}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer"
                        >
                          <Download className="w-4 h-4" />
                          {assignment.attachment_name || "Download Attachment"}
                        </button>
                      </div>
                    )}
                    {submission?.feedback && (
                      <p className="text-sm text-muted-foreground p-2 bg-muted/50 rounded-lg">
                        <strong>Feedback:</strong> {submission.feedback}
                      </p>
                    )}
                    {!isTeacher && isSubmitted && submission?.files?.length > 0 && (
                      <div className="space-y-1.5" data-testid={`my-submitted-files-${assignment.assignment_id}`}>
                        <p className="text-xs font-medium text-muted-foreground">Your submitted files ({submission.files.length}):</p>
                        <div className="flex flex-wrap gap-1.5">
                          {submission.files.map((f, i) => (
                            <button
                              key={i}
                              onClick={() => handleDownloadAttachment(`${API}/submissions/${submission.submission_id}/download?file_index=${i}`, f.original_filename)}
                              className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 px-2 py-1 rounded-full max-w-[200px] sm:max-w-[260px] transition-colors"
                              title={f.original_filename}
                            >
                              <Download className="w-3 h-3 shrink-0" />
                              <span className="truncate">{f.original_filename}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Submit Assignment Dialog */}
        <Dialog open={submitDialog} onOpenChange={(open) => { setSubmitDialog(open); if (!open) setSubmitFiles([]); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">{selectedAssignment && assignmentSubmissions[selectedAssignment.assignment_id] ? "Add More Files" : "Submit Assignment"}</DialogTitle>
              <DialogDescription>
                {selectedAssignment?.title} - Upload your work (multiple files supported)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Previously submitted files (visible only on resubmit) */}
              {selectedAssignment && assignmentSubmissions[selectedAssignment.assignment_id]?.files?.length > 0 && (
                <div className="rounded-xl border border-green-200 bg-green-50/60 p-3 space-y-2" data-testid="prev-submitted-files">
                  <p className="text-sm font-medium text-green-800 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" />
                    Previously submitted ({assignmentSubmissions[selectedAssignment.assignment_id].files.length} file{assignmentSubmissions[selectedAssignment.assignment_id].files.length > 1 ? "s" : ""}) — kept safe
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {assignmentSubmissions[selectedAssignment.assignment_id].files.map((f, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleDownloadAttachment(`${API}/submissions/${assignmentSubmissions[selectedAssignment.assignment_id].submission_id}/download?file_index=${i}`, f.original_filename)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-green-900 hover:bg-green-100 transition-colors text-left"
                        data-testid={`prev-file-${i}`}
                      >
                        <FileText className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate flex-1">{f.original_filename}</span>
                        <Download className="w-3.5 h-3.5 shrink-0 opacity-70" />
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-green-700">Adding new files below won't replace these — they will be added to your submission.</p>
                </div>
              )}

              <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                <input type="file" ref={assignmentFileRef} onChange={handleSubmitFilesChange} multiple className="hidden" data-testid="assignment-file-input" />
                <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} onChange={handleSubmitFilesChange} className="hidden" data-testid="assignment-camera-input" />
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  Choose files or take photos of your work
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Button variant="outline" onClick={() => assignmentFileRef.current?.click()} disabled={submittingAssignment} data-testid="submit-choose-file-btn">
                    <FileText className="w-4 h-4 mr-1" />
                    Choose Files
                  </Button>
                  <Button variant="outline" onClick={() => cameraInputRef.current?.click()} disabled={submittingAssignment} data-testid="submit-camera-btn">
                    <Camera className="w-4 h-4 mr-1" />
                    Take Photo
                  </Button>
                </div>
              </div>
              {submitFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{submitFiles.length} file(s) selected:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {submitFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-sm">
                        <span className="truncate max-w-[280px]">{file.name}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => removeSubmitFile(idx)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button
                onClick={handleSubmitAssignment}
                className="w-full rounded-full bg-primary text-primary-foreground"
                disabled={submitFiles.length === 0 || submittingAssignment}
                data-testid="confirm-submit-btn"
              >
                {submittingAssignment ? "Uploading..." : (selectedAssignment && assignmentSubmissions[selectedAssignment.assignment_id] ? `Add ${submitFiles.length} More File(s)` : `Submit ${submitFiles.length} File(s)`)}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Supported: PDF, Word, Images, and more. You can add multiple files.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Submissions Dialog (Teachers) */}
        <Dialog open={submissionsDialog} onOpenChange={setSubmissionsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                Submissions: {selectedAssignment?.title}
              </DialogTitle>
              <DialogDescription>
                View and grade student submissions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {loadingSubmissions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : selectedAssignmentSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No submissions yet</p>
                </div>
              ) : (
                  <div className="space-y-3">
                  {selectedAssignmentSubmissions.map((sub) => (
                    <div key={sub.submission_id} className="p-4 border border-border rounded-xl">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{sub.student_name}</p>
                          <p className="text-sm text-muted-foreground">{sub.student_email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Submitted: {format(new Date(sub.submitted_at), "MMM d, h:mm a")}
                            {sub.files && sub.files.length > 1 && ` · ${sub.files.length} files`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.grade !== null && sub.grade !== undefined ? (
                            <span className="px-2 py-1 text-sm bg-green-100 text-green-700 rounded-full">
                              {sub.grade}/{selectedAssignment?.total_points}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-full">
                              Not Graded
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {sub.files && sub.files.length > 0 ? (
                          sub.files.map((f, idx) => (
                            <Button key={idx} size="sm" variant="outline" className="rounded-full" onClick={() => handleDownloadAttachment(`${API}/submissions/${sub.submission_id}/download?file_index=${idx}`, f.original_filename)}>
                              <Download className="w-4 h-4 mr-1" />
                              {f.original_filename}
                            </Button>
                          ))
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleDownloadAttachment(`${API}/submissions/${sub.submission_id}/download`, sub.original_filename)}>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        )}
                        <Button size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={() => handleGradeSubmission(sub.submission_id, sub.grade, selectedAssignment?.total_points)}>
                          {sub.grade !== null ? "Update Grade" : "Grade"}
                        </Button>
                      </div>
                      {sub.feedback && (
                        <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                          <strong>Feedback:</strong> {sub.feedback}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
